export type PaperFormat = "APA" | "MLA" | "Chicago" | "CSE" | "Harvard";

export type InlineVariantKey = "narrative" | "parenthetical" | "bare";

export interface FormatConfig {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  marginTop: string;
  marginBottom: string;
  marginLeft: string;
  marginRight: string;
  titlePageRequired: boolean;
  pageNumbering: "top-right" | "bottom-center" | "top-center";
  headingStyles: {
    h1: string;
    h2: string;
    h3: string;
  };
}

export const formatConfigs: Record<PaperFormat, FormatConfig> = {
  APA: {
    fontFamily: "Times New Roman, serif",
    fontSize: "12pt",
    lineHeight: "2",
    marginTop: "1in",
    marginBottom: "1in",
    marginLeft: "1in",
    marginRight: "1in",
    titlePageRequired: true,
    pageNumbering: "top-right",
    headingStyles: {
      h1: "font-bold text-center",
      h2: "font-bold text-left",
      h3: "font-bold italic text-left",
    },
  },
  MLA: {
    fontFamily: "Times New Roman, serif",
    fontSize: "12pt",
    lineHeight: "2",
    marginTop: "1in",
    marginBottom: "1in",
    marginLeft: "1in",
    marginRight: "1in",
    titlePageRequired: false,
    pageNumbering: "top-right",
    headingStyles: {
      h1: "font-normal text-center",
      h2: "font-normal text-left",
      h3: "font-normal text-left",
    },
  },
  Chicago: {
    fontFamily: "Times New Roman, serif",
    fontSize: "12pt",
    lineHeight: "2",
    marginTop: "1in",
    marginBottom: "1in",
    marginLeft: "1in",
    marginRight: "1in",
    titlePageRequired: true,
    pageNumbering: "bottom-center",
    headingStyles: {
      h1: "font-bold text-center",
      h2: "font-bold text-left",
      h3: "font-normal italic text-left",
    },
  },
  CSE: {
    fontFamily: "Times New Roman, serif",
    fontSize: "12pt",
    lineHeight: "2",
    marginTop: "1in",
    marginBottom: "1in",
    marginLeft: "1in",
    marginRight: "1in",
    titlePageRequired: false,
    pageNumbering: "top-right",
    headingStyles: {
      h1: "font-bold text-center",
      h2: "font-bold text-left",
      h3: "font-normal text-left",
    },
  },
  Harvard: {
    fontFamily: "Arial, sans-serif",
    fontSize: "12pt",
    lineHeight: "1.5",
    marginTop: "1in",
    marginBottom: "1in",
    marginLeft: "1in",
    marginRight: "1in",
    titlePageRequired: true,
    pageNumbering: "bottom-center",
    headingStyles: {
      h1: "font-bold text-left",
      h2: "font-bold text-left",
      h3: "font-normal italic text-left",
    },
  },
};

const normalizeAuthorsArray = (rawAuthors: any): string[] => {
  if (!rawAuthors) return [];
  if (Array.isArray(rawAuthors)) {
    return rawAuthors.map((author) => String(author).trim()).filter(Boolean);
  }
  try {
    const parsed = JSON.parse(rawAuthors);
    if (Array.isArray(parsed)) {
      return parsed.map((author) => String(author).trim()).filter(Boolean);
    }
  } catch {
    /* ignore */
  }
  const parts = String(rawAuthors)
    .split(/,|&| and /i)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts : [String(rawAuthors).trim()].filter(Boolean);
};

const formatAuthorList = (authors: string[], format: PaperFormat): string => {
  if (!authors.length) return "Unknown";
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) {
    return format === "MLA" ? `${authors[0]} and ${authors[1]}` : `${authors[0]} & ${authors[1]}`;
  }
  return format === "MLA" ? `${authors[0]} et al.` : `${authors[0]} et al.`;
};

export const formatCitation = (
  citation: any,
  format: PaperFormat
): string => {
  const authorsArr = normalizeAuthorsArray(citation.authors);
  const authors = authorsArr.length ? authorsArr.join(", ") : "Unknown";
  const year = citation.year || "n.d.";
  const title = citation.title || "";

  switch (format) {
    case "APA":
      return `${authors} (${year}). ${title}. ${citation.publisher || ""}${citation.doi ? ` https://doi.org/${citation.doi}` : ""}`.trim();
    case "MLA":
      return `${authors}. "${title}." ${citation.publisher || ""}, ${year}.`.replace(/\s+\./g, ".").trim();
    case "Chicago":
      return `${authors}. ${title}. ${citation.publisher || ""}, ${year}.`.trim();
    case "CSE":
      return `${authors}. ${year}. ${title}. ${citation.publisher || ""}.`.trim();
    case "Harvard":
      return `${authors} (${year}) ${title}. ${citation.publisher || ""}.`.trim();
    default:
      return `${authors} (${year}). ${title}.`.trim();
  }
};

export const buildInlineReferenceVariants = (
  citation: any,
  format: PaperFormat
): Record<InlineVariantKey, string> => {
  const authors = formatAuthorList(normalizeAuthorsArray(citation.authors), format);
  const year = citation.year || "n.d.";

  return {
    narrative: `${authors} (${year})`,
    parenthetical: `(${authors}, ${year})`,
    bare: `${authors}, ${year}`,
  };
};

export const composeCombinedInlineReference = (
  citations: any[],
  format: PaperFormat
): string => {
  if (!citations.length) return "";
  const parts = citations.map((citation) => {
    const variants = buildInlineReferenceVariants(citation, format);
    return variants.bare;
  });
  return `(${parts.join("; ")})`;
};
